import { useCallback, useMemo, useState } from 'react';
import { type JSX } from 'react/jsx-runtime';
import { StorageClient } from '@atlaskit/frontend-utilities/StorageClient';

import { ChoreographedModal } from '../../../ui/ChoreographedModal';
import { PromoModal } from '../../../ui/PromoModal';
import { SideNavBanner } from '../../../ui/SideNavBanner';

// Created on first use: constructing a StorageClient on the server defines a global `window`.
let storageClient: StorageClient | undefined;
const getStorageClient = (): StorageClient =>
	(storageClient ??= new StorageClient('confluence-recent-downgrade-discount-modal', {
		storageEngine: 'localStorage',
	}));
const SEEN_KEY = 'seen';

const getSeenValue = (): boolean => getStorageClient().getItem(SEEN_KEY) === true;
const setSeenValue = (): void => getStorageClient().setItemWithExpiry(SEEN_KEY, true);

export type ConfluenceSideNavMessageProps = {
	entitlementId: string;
	placeOrderHref: string;
	edition: string;
	productName: string;
	shouldForceOpenModal: boolean;
	handleOnDismissed: () => void;
};

export const ConfluenceSideNavMessage = ({
	productName,
	edition,
	entitlementId,
	placeOrderHref,
	shouldForceOpenModal,
	handleOnDismissed,
}: ConfluenceSideNavMessageProps): JSX.Element => {
	// 'auto' opens wait for the choreographer so they don't collide with Confluence spotlights;
	// 'click' opens show immediately because the user asked for the modal.
	const [openedBy, setOpenedBy] = useState<'auto' | 'click' | null>(() =>
		shouldForceOpenModal && !getSeenValue() ? 'auto' : null,
	);

	const analyticsAttributes = useMemo(
		() => ({
			productName,
			edition,
			entitlementId,
		}),
		[productName, edition, entitlementId],
	);

	// Only mark as seen once the modal is actually shown, not while it waits for the choreographer.
	const handleModalShown = useCallback(() => {
		if (!getSeenValue()) {
			setSeenValue();
		}
	}, []);

	const handleBannerClick = useCallback(() => {
		setOpenedBy('click');
	}, []);

	const handleModalClose = useCallback(() => {
		setSeenValue();
		setOpenedBy(null);
	}, []);

	const handleDismissBanner = useCallback(() => {
		setSeenValue();
		handleOnDismissed();
	}, [handleOnDismissed]);

	return (
		<>
			<SideNavBanner
				onClick={handleBannerClick}
				onDismiss={handleDismissBanner}
				productName={productName}
				edition={edition}
				bannerName="confluenceRecentDowngradeDiscountBanner"
				analyticsAttributes={analyticsAttributes}
			/>
			<ChoreographedModal
				isOpen={openedBy !== null}
				shouldChoreograph={openedBy === 'auto'}
				onShown={handleModalShown}
			>
				{(canShow) => (
					<PromoModal
						isOpen={canShow}
						onClose={handleModalClose}
						productName={productName}
						edition={edition}
						placeOrderHref={placeOrderHref}
						modalName={'confluenceRecentDowngradeDiscountModal'}
					/>
				)}
			</ChoreographedModal>
		</>
	);
};
